/**
 * CustomerPicker — single-select customer chooser with search and paging.
 *
 * The mechanics (search, paging, keyboard, click-outside) moved to EntityPicker when a second
 * surface needed the same widget for escalation policies. This file is now the customer-specific
 * vocabulary and nothing else: same export, same props, same behaviour as before, so no call site
 * changed. Copying EntityPicker and adjusting its strings would have produced two ~190-line files
 * that drift the first time a keyboard bug is fixed in one of them — the fork Architecture
 * Principle 8 exists to prevent.
 *
 * WHY NOT TenantScopePicker
 * -------------------------
 * That component is a 509-line MULTI-select grants editor over tenants *and* their instances,
 * emitting {tenantId, instanceIds} tuples. Reusing it for "pick one customer" would mean disabling
 * most of it and reinterpreting its value shape — more coupling than code saved.
 */
import React from 'react';
import { Building2 } from 'lucide-react';
import { EntityPicker } from './EntityPicker';

const label = (c) => (c ? (c.displayName || c.name || c.id) : null);

export function CustomerPicker({
  customers = [],
  value,
  onChange,
  loading = false,
  disabled = false,
  ariaLabel = 'Select a customer',
}) {
  return (
    <EntityPicker
      items={customers}
      value={value}
      onChange={onChange}
      loading={loading}
      disabled={disabled}
      ariaLabel={ariaLabel}
      icon={<Building2 size={14} className="text-v2-ink-muted shrink-0" />}
      getId={(c) => c.id}
      getLabel={label}
      getSearchText={(c) => [c.displayName, c.name, c.id].filter(Boolean).join(' ')}
      placeholder="Choose a customer"
      loadingText="Loading customers…"
      searchPlaceholder="Search by name"
      emptyMessage={(q, total) => (q
        ? `No customer matches “${q}”. Try part of the name, or clear the search to see all ${total}.`
        : 'No customers are available to you. Ask a Platform Admin to grant access.')}
    />
  );
}

export default CustomerPicker;
